import { MeasurementsQueryResult } from '../../../timeSeries/types'
import { ExtendedSeriesWithMeasurements } from '@/types/meters'
import HighchartsChart from '@/components/Charts/HighchartsChart'
import Highcharts from 'highcharts'
import { parseFirstMeasurementAggOrValue } from '../utils/parseFirstMeasurementAggOrValue'
import { SeriesFilter } from '../../../timeSeries/timeSeriesFilters'
import {
    useTimeSeriesMeasurements,
    TimeSeriesAggregateOptions,
    SeriesParsingOptions,
} from '../../../timeSeries/useTimeSeriesMeasurements'
import { Skeleton } from '@mui/material'
import { CommonChartProps, useCommonChartOptions } from '../commonChartOptions'

type DualAxisAllowedType = 'bar' | 'line' | 'spline'

type PlotSeriesOptions = Omit<Highcharts.PlotSeriesOptions, 'data'>

interface AxisFormatOptions {
    type: DualAxisAllowedType
    title?: string
    plotOptions?: PlotSeriesOptions
}

const translateToHichartsType = (type: DualAxisAllowedType): string => (type === 'bar' ? 'column' : type)

interface TimeSeriesDualAxesChartProps extends CommonChartProps {
    primaryAxis: AxisFormatOptions & { series: MeasurementsQueryResult[] }
    secondaryAxis: AxisFormatOptions & { series: MeasurementsQueryResult[] }
}

const chartFormatOptions: Highcharts.Options = {
    title: {
        text: '',
    },
    chart: {
        backgroundColor: 'transparent',
    },
    tooltip: {
        dateTimeLabelFormats: {
            day: '%e %B %Y',
            week: '%e %b %Y',
            month: '%b %Y',
            year: '%Y',
        },
        shared: true,
        valueDecimals: 2,
    },
    yAxis: {
        title: {
            text: '',
        },
    },
    plotOptions: {
        series: {
            animation: false,
            stacking: 'normal',
        },
    },
}

export const formatUnit = (series: ExtendedSeriesWithMeasurements): string => {
    const unit = series.unit
    return unit ? ` ${unit}` : ''
}

const formatToHighchartsSeries = (
    series: ExtendedSeriesWithMeasurements,
    axisIndex: number,
    axisType: DualAxisAllowedType,
    plotOptions?: PlotSeriesOptions
) => {
    const unit = formatUnit(series)
    return {
        name: series.label || series.meterName,
        color: series.color,
        data: series.measurements.map((m) => ({
            x: new Date(m.timestamp).getTime(),
            y: parseFirstMeasurementAggOrValue(m),
            unit,
        })),
        type: translateToHichartsType(axisType),
        yAxis: axisIndex,
        tooltip: {
            valueSuffix: unit,
        },

        ...plotOptions,
    }
}

const formatTitle = (title: string | undefined, series: ExtendedSeriesWithMeasurements): string | undefined => {
    if (!title) {
        return undefined
    }
    const unit = series.unit
    return unit ? `${title} [${unit}]` : title
}

const DualAxesChart: React.FC<TimeSeriesDualAxesChartProps> = ({
    primaryAxis,
    secondaryAxis,
    disableLegend,
    slug,
    exporting,
}) => {
    const primaryAxisSeries = primaryAxis.series.map((s) => s.data)
    const secondaryAxisSeries = secondaryAxis.series.map((s) => s.data)
    const commonOptions = useCommonChartOptions()
    return (
        <HighchartsChart
            title="Bar Chart"
            type="bar"
            options={{
                ...commonOptions,
                ...chartFormatOptions,

                yAxis: [
                    {
                        title: {
                            text: formatTitle(primaryAxis.title, primaryAxisSeries[0]),
                        },
                    },
                    {
                        title: {
                            text: formatTitle(secondaryAxis.title, secondaryAxisSeries[0]),
                        },

                        opposite: true,
                    },
                ],
                series: [
                    ...primaryAxisSeries.map((s) =>
                        formatToHighchartsSeries(s, 0, primaryAxis.type, primaryAxis.plotOptions)
                    ),
                    ...secondaryAxisSeries.map((s) =>
                        formatToHighchartsSeries(s, 1, secondaryAxis.type, secondaryAxis.plotOptions)
                    ),
                ],
                legend: {
                    enabled: !disableLegend,
                },
            }}
            exporting={exporting}
            slug={slug}
        />
    )
}

interface TimeSeriesDualAxesChartWrapperProps extends CommonChartProps {
    primaryAxis: AxisFormatOptions & {
        filters: SeriesFilter
        parsingOptions?: SeriesParsingOptions
        aggregateOptions?: TimeSeriesAggregateOptions
    }
    secondaryAxis: AxisFormatOptions & {
        filters: SeriesFilter
        parsingOptions?: SeriesParsingOptions
        aggregateOptions?: TimeSeriesAggregateOptions
    }
}

const DualAxesChartWrapper: React.FC<TimeSeriesDualAxesChartWrapperProps> = (props) => {
    const { primaryAxis, secondaryAxis, ...rest } = props
    const primaryAxisSeries = useTimeSeriesMeasurements(
        primaryAxis.filters,
        primaryAxis.parsingOptions,
        primaryAxis.aggregateOptions
    )
    const secondaryAxisSeries = useTimeSeriesMeasurements(
        secondaryAxis.filters,
        secondaryAxis.parsingOptions,
        secondaryAxis.aggregateOptions
    )
    const isLoading = primaryAxisSeries.isLoading || secondaryAxisSeries.isLoading
    if (isLoading) {
        return <Skeleton variant="rectangular" sx={{ height: 300 }} />
    }
    return (
        <DualAxesChart
            primaryAxis={{ ...primaryAxis, series: primaryAxisSeries.series }}
            secondaryAxis={{ ...secondaryAxis, series: secondaryAxisSeries.series }}
            {...rest}
        />
    )
}

export default DualAxesChartWrapper
